import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { syncProductsToChannable, getChannableSyncStatus, getChannableWebhookEvents } from "@/lib/channable.functions";
import { CheckCircle2, AlertCircle, Clock, ExternalLink, Copy, Webhook } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/kanalen")({
  head: () => ({ meta: [{ title: "Admin — Verkoopkanalen" }, { name: "robots", content: "noindex" }] }),
  component: AdminChannels,
});

function AdminChannels() {
  const { isAdmin, loading } = useAuth();
  const sync = useServerFn(syncProductsToChannable);
  const status = useServerFn(getChannableSyncStatus);
  const webhookEvents = useServerFn(getChannableWebhookEvents);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["channable-status"],
    queryFn: () => status({}),
    enabled: isAdmin,
  });

  const { data: events } = useQuery({
    queryKey: ["channable-webhook-events"],
    queryFn: () => webhookEvents({}),
    enabled: isAdmin,
    refetchInterval: 15000,
  });

  const syncMut = useMutation({
    mutationFn: () => sync({}),
    onSuccess: (r) => {
      toast.success(`Sync klaar: ${r.pushed} verstuurd, ${r.skipped} ongewijzigd, ${r.failed} mislukt`);
      qc.invalidateQueries({ queryKey: ["channable-status"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard-stats"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const products = data?.products ?? [];
  const neverSynced = products.filter((p: any) => !p.channable_last_synced_at).length;
  const withErrors = products.filter((p: any) => p.channable_last_error).length;
  const synced = products.length - neverSynced - withErrors;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Verkoopkanalen</h1>
        <p className="text-muted-foreground">Beheer al je distributiekanalen op één plek</p>
      </div>

      {/* Channable - primary hub */}
      <ChannelCard
        title="Channable"
        subtitle="Feed-platform — distribueert producten naar alle aangesloten marketplaces"
        status="connected"
        action={
          <Button onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
            {syncMut.isPending ? "Bezig…" : "Sync nu"}
          </Button>
        }
      >
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MiniStat icon={CheckCircle2} label="Gesynchroniseerd" value={synced} color="text-emerald-600" />
          <MiniStat icon={Clock} label="Nog niet" value={neverSynced} color="text-muted-foreground" />
          <MiniStat icon={AlertCircle} label="Met fout" value={withErrors} color="text-destructive" />
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Laatste 100 producten bekijken</summary>
          <div className="mt-3 border rounded overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0">
                <tr><th className="text-left p-2">Naam</th><th className="text-left p-2">Laatste sync</th><th className="text-left p-2">Status</th></tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={3} className="p-3 text-center text-muted-foreground">Laden…</td></tr>
                ) : products.map((p: any) => (
                  <tr key={p.id} className="border-t">
                    <td className="p-2">{p.name}</td>
                    <td className="p-2">{p.channable_last_synced_at ? new Date(p.channable_last_synced_at).toLocaleString("nl-NL") : "—"}</td>
                    <td className="p-2">
                      {p.channable_last_error ? <span className="text-destructive">{p.channable_last_error}</span>
                        : p.channable_synced_hash ? <span className="text-emerald-600">OK</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </ChannelCard>

      <WebhookCard events={events?.events ?? []} />



      {/* Sub-channels routed through Channable */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Aangesloten marketplaces (via Channable)</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Deze kanalen worden gevoed via Channable. Inschakelen/configureren doe je in het Channable-dashboard.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <SubChannel name="Google Shopping / Merchant Center" desc="Producten verschijnen in Google zoekresultaten" docUrl="https://www.channable.com/integrations/google-shopping" />
          <SubChannel name="Bol.com" desc="Verkoop op de grootste Nederlandse marketplace" docUrl="https://www.channable.com/integrations/bol-com" />
          <SubChannel name="Marktplaats Pro (Admarkt)" desc="CPC-advertenties op Marktplaats" docUrl="https://www.channable.com/integrations/marktplaats-admarkt" />
          <SubChannel name="eBay" desc="Internationale marketplace" docUrl="https://www.channable.com/integrations/ebay" />
          <SubChannel name="Amazon" desc="Wereldwijde marketplace" docUrl="https://www.channable.com/integrations/amazon-marketplace-integration" />
          <SubChannel name="Facebook / Instagram Shopping" desc="Producten op social media" docUrl="https://www.channable.com/integrations/facebook-dynamic-ads" />
        </div>
      </div>
    </div>
  );
}

function ChannelCard({ title, subtitle, status, action, children }: { title: string; subtitle: string; status: "connected" | "pending" | "disconnected"; action?: React.ReactNode; children?: React.ReactNode }) {
  const statusColor = status === "connected" ? "bg-emerald-100 text-emerald-800" : status === "pending" ? "bg-amber-100 text-amber-800" : "bg-muted text-muted-foreground";
  const statusLabel = status === "connected" ? "Verbonden" : status === "pending" ? "Configuratie nodig" : "Niet verbonden";
  return (
    <div className="border rounded-xl bg-card p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">{title}</h2>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>{statusLabel}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, color }: { icon: any; label: string; value: number; color: string }) {
  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center gap-2">
        <Icon className={`size-4 ${color}`} />
        <span className={`text-xl font-bold ${color}`}>{value}</span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function SubChannel({ name, desc, docUrl }: { name: string; desc: string; docUrl: string }) {
  return (
    <div className="border rounded-lg p-4 flex items-start justify-between gap-3">
      <div>
        <div className="font-semibold">{name}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
      <a href={docUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1 shrink-0">
        Docs <ExternalLink className="size-3" />
      </a>
    </div>
  );
}

function WebhookCard({ events }: { events: any[] }) {
  const webhookUrl = typeof window !== "undefined"
    ? `${window.location.origin}/api/public/hooks/channable-webhook`
    : "/api/public/hooks/channable-webhook";

  const copy = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook-URL gekopieerd");
  };

  return (
    <div className="border rounded-xl bg-card p-6">
      <div className="flex items-center gap-2 mb-2">
        <Webhook className="size-5 text-primary" />
        <h2 className="text-xl font-bold">Channable webhooks</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Plak deze URL in Channable → Settings → Webhooks om automatisch te syncen bij item- en order-events.
        Stel daar ook een secret in en sla die op als <code className="bg-muted px-1 rounded">CHANNABLE_WEBHOOK_SECRET</code> voor HMAC-verificatie.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <code className="flex-1 bg-muted px-3 py-2 rounded text-xs break-all">{webhookUrl}</code>
        <Button size="sm" variant="outline" onClick={copy}>
          <Copy className="size-3 mr-1" /> Kopiëren
        </Button>
      </div>

      <details>
        <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
          Laatste 50 ontvangen webhook-events
        </summary>
        <div className="mt-3 border rounded overflow-hidden max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="text-left p-2">Ontvangen</th>
                <th className="text-left p-2">Event</th>
                <th className="text-left p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={3} className="p-3 text-center text-muted-foreground">Nog geen events ontvangen</td></tr>
              ) : events.map((e) => (
                <tr key={e.id} className="border-t">
                  <td className="p-2">{new Date(e.received_at).toLocaleString("nl-NL")}</td>
                  <td className="p-2 font-mono">{e.event_type}</td>
                  <td className="p-2">
                    {e.error ? <span className="text-destructive">{e.error}</span>
                      : e.processed ? <span className="text-emerald-600">Verwerkt</span>
                      : <span className="text-muted-foreground">In behandeling…</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
